import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook pour créer une expérience immersive au scroll (Mobile uniquement)
 * 
 * Comportements:
 * - Scroll DOWN → tout disparaît (header, nav, bottom nav, top banner)
 * - Scroll UP → header + nav + bottom nav réapparaissent
 * - Retour TOP (scrollY < 5px) → le bandeau orange réapparaît aussi
 * 
 * Usage:
 *   const { isHidden, isAtTop, shouldShowBanner } = useImmersiveScroll();
 */
export function useImmersiveScroll(options = {}) {
  const {
    threshold = 20, // Pixels minimum avant de déclencher (évite micro-scroll)
    topThreshold = 5, // Seuil pour considérer "en haut de page"
    mobileOnly = true,
    mobileBreakpoint = 767,
  } = options;

  const [isHidden, setIsHidden] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [scrollDirection, setScrollDirection] = useState('up'); // 'up' ou 'down'
  
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const isInputFocused = useRef(false);

  // Écouter le focus sur les inputs pour désactiver l'animation
  useEffect(() => {
    const handleFocusIn = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        isInputFocused.current = true;
        setIsHidden(false); // Afficher tout quand un input est focus
      }
    };
    
    const handleFocusOut = () => {
      isInputFocused.current = false;
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const handleScroll = useCallback(() => {
    if (ticking.current) return;
    if (isInputFocused.current) return; // Ignorer si input focus

    ticking.current = true;

    requestAnimationFrame(() => {
      // Vérifier si on est sur mobile
      if (mobileOnly && window.innerWidth > mobileBreakpoint) {
        setIsHidden(false);
        setIsAtTop(true);
        ticking.current = false;
        return;
      }

      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY.current;

      // Mettre à jour isAtTop
      const atTop = currentScrollY < topThreshold;
      setIsAtTop(atTop);

      // Si on est tout en haut, tout afficher
      if (atTop) {
        setIsHidden(false);
        setScrollDirection('up');
        lastScrollY.current = currentScrollY;
        ticking.current = false;
        return;
      }

      // Détecter la direction avec threshold
      if (Math.abs(delta) > threshold) {
        if (delta > 0) {
          // Scroll vers le bas → masquer
          setIsHidden(true);
          setScrollDirection('down');
        } else {
          // Scroll vers le haut → afficher (sauf banner orange)
          setIsHidden(false);
          setScrollDirection('up');
        }
        lastScrollY.current = currentScrollY;
      }

      ticking.current = false;
    });
  }, [threshold, topThreshold, mobileOnly, mobileBreakpoint]);

  useEffect(() => {
    // Initialiser l'état
    setIsAtTop(window.scrollY < topThreshold);

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    const handleResize = () => {
      if (window.innerWidth > mobileBreakpoint) {
        setIsHidden(false);
        setIsAtTop(true);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleScroll, mobileBreakpoint, topThreshold]);

  // shouldShowBanner: visible uniquement si on est tout en haut
  const shouldShowBanner = isAtTop;

  return { 
    isHidden, 
    isAtTop, 
    scrollDirection,
    shouldShowBanner,
  };
}

export default useImmersiveScroll;
